import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useVehicleFeatures } from '../hooks/useVehicleFeatures';
import { VehicleFeature } from '../../api/services/vehicleFeatureService';

const { Title, Text } = Typography;
const ORANGE = '#fe5a1d';

const VehicleFeatures: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    features,
    loading,
    modalVisible,
    editingFeature,
    submitting,
    setModalVisible,
    handleCreate,
    handleDelete,
    openCreateModal,
    refetch,
  } = useVehicleFeatures();

  useEffect(() => {
    if (modalVisible && editingFeature) {
      form.setFieldsValue({
        name: editingFeature.name,
        category: editingFeature.category,
        sortOrder: editingFeature.sortOrder ?? 0,
      });
    } else if (modalVisible && !editingFeature) {
      form.resetFields();
      form.setFieldsValue({ sortOrder: 0 });
    }
  }, [modalVisible, editingFeature, form]);

  const onFinish = (values: any) => {
    handleCreate(values);
  };

  const columns = [
    {
      title: t('admin.vehicle_features.col_name'),
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => (
        <Space>
          <BgColorsOutlined style={{ color: ORANGE }} />
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.vehicle_features.col_category'),
      dataIndex: 'category',
      key: 'category',
      render: (v: string | undefined) =>
        v ? (
          <Tag color="blue">{v}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('admin.vehicle_features.col_sort_order'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      render: (v: number) => <Text type="secondary">{v ?? 0}</Text>,
    },
    {
      title: t('admin.vehicle_features.col_created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <Text type="secondary">{new Date(v).toLocaleDateString()}</Text>
      ),
    },
    {
      title: t('admin.vehicle_features.col_actions'),
      key: 'actions',
      render: (_: any, record: VehicleFeature) => (
        <Popconfirm
          title={t('admin.vehicle_features.delete_confirm')}
          description={t('admin.vehicle_features.delete_desc')}
          onConfirm={() => handleDelete(record.id)}
          okText={t('admin.vehicle_features.btn_delete')}
          okType="danger"
          cancelText={t('admin.vehicle_features.btn_cancel')}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{t('admin.vehicle_features.title')}</Title>
          <Text type="secondary">{t('admin.vehicle_features.subtitle')}</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading} className="admin-filter-btn">
            {t('admin.vehicle_features.btn_refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="ant-btn-primary admin-filter-btn admin-filter-btn-primary"
          >
            {t('admin.vehicle_features.btn_add')}
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Card bordered={false} className="admin-table-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={features}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('admin.vehicle_features.empty_text') }}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title={
          <Space>
            <BgColorsOutlined style={{ color: 'var(--admin-primary)' }} />
            {t('admin.vehicle_features.modal_add_title')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        okText={t('admin.vehicle_features.modal_create')}
        okButtonProps={{ className: 'ant-btn-primary' }}
        cancelText={t('admin.vehicle_features.btn_cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t('admin.vehicle_features.label_name')}
            rules={[{ required: true, message: t('admin.vehicle_features.validation_name') }]}
          >
            <Input placeholder={t('admin.vehicle_features.ph_name')} />
          </Form.Item>
          <Form.Item name="category" label={t('admin.vehicle_features.label_category')}>
            <Input placeholder={t('admin.vehicle_features.ph_category')} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('admin.vehicle_features.label_sort_order')}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default VehicleFeatures;
